// Icon-Picker fuer den FloatingPropertyBar bei icon-Selection.
// Default-Grid: 16 statisch importierte Icons (tree-shakeable, ~30 kB).
// Long-Tail: Lazy-Load via dynamic import("lucide-react") sobald gesucht wird.

import { type JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type LucideComp, ensureIcon, getAllLucideNames, getIcon } from "./icon-cache.js";
import { ICON_DEFAULT_NAMES, searchDefaults } from "./icon-defaults.js";
import "./IconPicker.css";

interface IconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
}

interface LucideMetaIcon {
  name: string;
  tags: readonly string[];
}

export function IconPicker({ value, onChange }: IconPickerProps): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [longTail, setLongTail] = useState<LucideMetaIcon[]>([]);
  const [loadingLongTail, setLoadingLongTail] = useState(false);

  useEffect(() => {
    if (!query.trim()) return;
    if (longTail.length > 0 || loadingLongTail) return;
    setLoadingLongTail(true);
    void loadLongTailIcons().then((icons) => {
      setLongTail(icons);
      setLoadingLongTail(false);
    });
  }, [query, longTail.length, loadingLongTail]);

  const defaultsFiltered = searchDefaults(query);
  const longTailFiltered = query.trim()
    ? longTail
        .filter(
          (i) =>
            i.name.toLowerCase().includes(query.toLowerCase()) ||
            i.tags.some((tag) => tag.toLowerCase().includes(query.toLowerCase())),
        )
        .slice(0, 30)
    : [];

  const longTailWithoutDefaults = longTailFiltered.filter(
    (i) => !(ICON_DEFAULT_NAMES as readonly string[]).includes(i.name),
  );

  return (
    <div className="icon-picker">
      <input
        type="search"
        className="icon-picker-search"
        placeholder={t("inspector.icon.searchPlaceholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={t("inspector.icon.searchAria")}
      />
      <div className="icon-picker-grid" role="radiogroup">
        {defaultsFiltered.map((name) => (
          <IconButton key={name} name={name} active={value === name} onPick={onChange} />
        ))}
        {longTailWithoutDefaults.map((meta) => (
          <IconButton
            key={meta.name}
            name={meta.name}
            active={value === meta.name}
            onPick={onChange}
          />
        ))}
        {loadingLongTail && <span className="icon-picker-loading">…</span>}
        {!loadingLongTail &&
          defaultsFiltered.length === 0 &&
          longTailWithoutDefaults.length === 0 && (
            <span className="icon-picker-empty">{t("inspector.icon.noResults")}</span>
          )}
      </div>
    </div>
  );
}

interface IconButtonProps {
  name: string;
  active: boolean;
  onPick: (n: string) => void;
}

function IconButton({ name, active, onPick }: IconButtonProps): JSX.Element {
  const [Cmp, setCmp] = useState<LucideComp | undefined>(() => getIcon(name));
  useEffect(() => {
    if (getIcon(name)) {
      setCmp(() => getIcon(name));
      return;
    }
    let cancelled = false;
    void ensureIcon(name).then((resolved) => {
      if (!cancelled) setCmp(() => resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [name]);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={name}
      title={name}
      className={`icon-picker-btn ${active ? "is-active" : ""}`}
      onClick={() => onPick(name)}
    >
      {Cmp ? <Cmp size={20} /> : <span className="icon-picker-fallback">{name.charAt(0)}</span>}
    </button>
  );
}

async function loadLongTailIcons(): Promise<LucideMetaIcon[]> {
  const names = await getAllLucideNames();
  return names.map((name) => ({ name, tags: [name.toLowerCase()] }));
}
